import React from "react";
import { Formik, FormikErrors } from "formik";
import { useMutation } from "@apollo/client";
import { CREATE_REFERRAL } from "../../queries/screener";
import { logEvent } from "../../actions";
import { reportError } from "../../utils/errorUtils";
import { AgeBand, SelectedProvider } from "../../pages/Screener";
import { ScreenerResult } from "./scoring";
import { buildConnectToCareReferral } from "./referral";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

type ConnectFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

const INITIAL_VALUES: ConnectFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: ""
};

function validate(values: ConnectFormValues): FormikErrors<ConnectFormValues> {
  const errors: FormikErrors<ConnectFormValues> = {};
  if (!values.firstName.trim()) errors.firstName = "Required";
  if (!values.lastName.trim()) errors.lastName = "Required";
  if (!values.email.trim()) {
    errors.email = "Required";
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Invalid email address";
  }
  return errors;
}

type ConnectToCareFormProps = {
  readonly provider: SelectedProvider;
  readonly result: ScreenerResult;
  readonly ageBand: AgeBand;
  readonly onSubmitted: () => void;
};

export default function ConnectToCareForm({ provider, result, ageBand, onSubmitted }: ConnectToCareFormProps) {
  const [createReferral] = useMutation(CREATE_REFERRAL);

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validate={validate}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const input = buildConnectToCareReferral(provider, result, ageBand, values);
          await createReferral({ variables: { input } });
          logEvent("connect_submitted");
          onSubmitted();
        } catch (error) {
          reportError(error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
        <form className="screener-connect" onSubmit={handleSubmit} noValidate>
          <div className="screener-connect__row">
            <ConnectField
              name="firstName"
              label="First name *"
              value={values.firstName}
              error={touched.firstName ? errors.firstName : undefined}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ConnectField
              name="lastName"
              label="Last name *"
              value={values.lastName}
              error={touched.lastName ? errors.lastName : undefined}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          <ConnectField
            name="email"
            label="Email address *"
            type="email"
            value={values.email}
            error={touched.email ? errors.email : undefined}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <ConnectField
            name="phoneNumber"
            label="Phone (optional)"
            type="tel"
            value={values.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <button type="submit" className="screener-button screener-button--attention" disabled={isSubmitting}>
            Submit →
          </button>
        </form>
      )}
    </Formik>
  );
}

type ConnectFieldProps = {
  readonly name: keyof ConnectFormValues;
  readonly label: string;
  readonly value: string;
  readonly type?: string;
  readonly error?: string;
  readonly onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
};

function ConnectField({ name, label, value, type = "text", error, onChange, onBlur }: ConnectFieldProps) {
  return (
    <label className="screener-connect__field">
      <input
        className={`screener-connect__input${error ? " screener-connect__input--error" : ""}`}
        name={name}
        type={type}
        placeholder={label}
        aria-label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && <span className="screener-connect__error">{error}</span>}
    </label>
  );
}
